import React, { useMemo, useState } from 'react'
import {
  Button,
  Descriptions,
  Input,
  Modal,
  Select,
  Tooltip,
  message,
} from 'antd'
import { FlagOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { createProductIssue } from 'src/utils/productIssueApi'
import {
  PRODUCT_ISSUE_CATEGORIES,
  PRODUCT_ISSUE_EXPLANATION_MAX_LENGTH,
  PRODUCT_ISSUE_SCOPE,
  buildProductIssueContext,
  getProductIssueSubmitFeedback,
} from 'src/utils/productIssue'

const { TextArea } = Input
const { Option } = Select

const REPORT_ICON_GREEN = '#52c41a'

/** Match Shopping List Edit/Delete action buttons (antd v3 default size). */
const IconActionButton = styled(Button)`
  margin: 0;
  min-width: 40px;
  width: 40px;
  height: 32px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.65);

  .anticon {
    color: ${REPORT_ICON_GREEN};
  }
`

const LabelActionButton = styled(Button)`
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 400 !important;
  height: 32px;
  padding: 0 10px;

  .anticon {
    color: ${REPORT_ICON_GREEN};
    margin-right: 6px;
    display: inline-flex;
    align-items: center;
  }
`

/**
 * Compact "Report product issue" control for product / order / invoice rows.
 * Opens a modal; OMS auto-fills supplier + product snapshot from `context`.
 */
export default function ReportProductIssueButton({
  venueId,
  context,
  product,
  order,
  invoice,
  supplier,
  supplierTitle,
  sourceScreen,
  size,
  type,
  label = 'Report product issue',
  iconOnly = false,
  /** Stretch to parent width (Shopping List action column). */
  block = false,
  disabled = false,
  style,
  className,
  tooltipPlacement = 'top',
}) {
  const buttonType = type || (iconOnly ? 'dashed' : 'link')
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState(undefined)
  const [explanation, setExplanation] = useState('')
  const [saving, setSaving] = useState(false)

  const payload = useMemo(
    () =>
      context ||
      buildProductIssueContext({
        product,
        order,
        invoice,
        supplier,
        supplierTitle,
        sourceScreen,
      }),
    [context, product, order, invoice, supplier, supplierTitle, sourceScreen],
  )

  const snap = payload?.productSnapshot || {}
  const canOpen = Boolean(venueId) && !disabled

  const reset = () => {
    setCategory(undefined)
    setExplanation('')
    setSaving(false)
  }

  const handleOpen = e => {
    if (e?.stopPropagation) e.stopPropagation()
    if (!canOpen) return
    reset()
    setOpen(true)
  }

  const handleCancel = () => {
    setOpen(false)
    reset()
  }

  const handleSubmit = async () => {
    if (!category) {
      message.warning('Select an issue category')
      return
    }
    setSaving(true)
    try {
      const result = await createProductIssue(venueId, {
        ...payload,
        category,
        explanation: explanation.trim(),
      })
      message.success(
        getProductIssueSubmitFeedback({
          routing: result?.routing,
          scope: PRODUCT_ISSUE_SCOPE.PRODUCT,
        }),
      )
      setOpen(false)
      reset()
    } catch (error) {
      message.error(error.message || 'Could not submit the product issue')
      setSaving(false)
    }
  }

  // antd v3: put icons as children (icon= ReactNode is antd 4+). Match Edit/Delete actions.
  const trigger = iconOnly ? (
    <Tooltip title={label} placement={tooltipPlacement}>
      <IconActionButton
        type={buttonType}
        size={size}
        onClick={handleOpen}
        disabled={!canOpen}
        style={{
          ...(block ? { width: '100%', margin: '3px 3px 3px 0' } : null),
          ...style,
        }}
        className={className}
        aria-label={label}
        data-testid='report-product-issue-button'
      >
        <FlagOutlined />
      </IconActionButton>
    </Tooltip>
  ) : (
    <LabelActionButton
      type={buttonType}
      size={size || 'small'}
      onClick={handleOpen}
      disabled={!canOpen}
      style={style}
      className={className}
      data-testid='report-product-issue-button'
    >
      <FlagOutlined />
      <span>{label}</span>
    </LabelActionButton>
  )

  return (
    <>
      {trigger}
      <Modal
        title='Report product issue'
        visible={open}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText='Submit report'
        confirmLoading={saving}
        destroyOnClose
        width={520}
        okButtonProps={{ disabled: !category }}
      >
        <p style={{ marginTop: 0, color: '#595959' }}>
          Flag a catalogue data error without leaving this screen. Supplier and
          product details are filled in for you.
        </p>

        <Descriptions
          size='small'
          column={1}
          bordered
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label='Supplier'>
            {payload.supplierTitle || '—'}
          </Descriptions.Item>
          <Descriptions.Item label='Product code'>
            {snap.productCode || '—'}
          </Descriptions.Item>
          <Descriptions.Item label='Description'>
            {snap.description || '—'}
          </Descriptions.Item>
          <Descriptions.Item label='Price'>
            {snap.price || '—'}
          </Descriptions.Item>
          <Descriptions.Item label='Pack size'>
            {snap.packSize || '—'}
          </Descriptions.Item>
          <Descriptions.Item label='VAT rate'>
            {snap.vatRate || '—'}
          </Descriptions.Item>
          <Descriptions.Item label='Selling unit'>
            {snap.sellingUnit || '—'}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>
            Issue category <span style={{ color: '#cf1322' }}>*</span>
          </div>
          <Select
            placeholder='Select an issue category'
            value={category}
            onChange={setCategory}
            style={{ width: '100%' }}
            data-testid='report-product-issue-category'
          >
            {PRODUCT_ISSUE_CATEGORIES.map(item => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        </div>

        <div>
          <div style={{ marginBottom: 6, fontWeight: 500 }}>
            Explanation <span style={{ color: '#8c8c8c' }}>(optional)</span>
          </div>
          <TextArea
            rows={3}
            value={explanation}
            onChange={e => setExplanation(e.target.value)}
            maxLength={PRODUCT_ISSUE_EXPLANATION_MAX_LENGTH}
            placeholder='Short note for the supplier or your team'
            data-testid='report-product-issue-explanation'
          />
        </div>
      </Modal>
    </>
  )
}
